import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { startRegistration } from '@simplewebauthn/browser';
import type { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSInternalSpinner } from '@repo/sds/_internal/SDSInternalSpinner';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';
import { withFinalizer } from '@repo/ui/utils/with-finalizer';

import passkeySetupIntro from './assets/passkey-setup-intro.svg';
import { MfaHero } from './mfa-hero';
import { MfaInvalidCodeDialog } from './mfa-invalid-code-dialog';
import { clearVisualSmsEnrollment, consumeReplaceSmsIntent } from './mfa-method-picker-utils';
import { MfaSettingsPage } from './mfa-settings-page';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

export function MfaPasskeySetupDialogs({
  isMobile,
  onBackToMethods,
  onCancel,
  onCreated,
  onRegister,
  optionsJson,
  t,
}: {
  isMobile: boolean;
  onBackToMethods: () => void;
  onCancel: () => void;
  onCreated: () => void;
  onRegister: (responseJson: string) => Promise<boolean | 'backup'>;
  optionsJson: string;
  t: TFunction<'common'>;
}) {
  const [phase, setPhase] = useState<'intro' | 'creating' | 'failed'>('intro');
  const onCreatedRef = useRef(onCreated);
  const onRegisterRef = useRef(onRegister);
  const optionsJsonRef = useRef(optionsJson);
  const inFlightRef = useRef(false);

  // Kept in sync in an effect (not during render) so the async ceremony below
  // always reads the latest callback/props without capturing a stale closure.
  useEffect(() => {
    onCreatedRef.current = onCreated;
    onRegisterRef.current = onRegister;
    optionsJsonRef.current = optionsJson;
  }, [onCreated, onRegister, optionsJson]);

  const createPasskey = () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;

    // Start the ceremony in the same synchronous turn as the click (before
    // setState) so user activation and Playwright's credentials interceptor
    // both see navigator.credentials.create().
    let ceremony: ReturnType<typeof startRegistration>;
    try {
      const options = JSON.parse(optionsJsonRef.current) as PublicKeyCredentialCreationOptionsJSON;
      ceremony = startRegistration({ optionsJSON: options });
    } catch {
      inFlightRef.current = false;
      setPhase('failed');
      return;
    }

    setPhase('creating');
    void (async () => {
      await withFinalizer(
        async () => {
          try {
            const attestation = await ceremony;
            const result = await onRegisterRef.current(JSON.stringify(attestation));
            if (!result) {
              setPhase('failed');
              return;
            }
            toast.success(t('security.mfa.passkeyEnabledToast'));
            if (consumeReplaceSmsIntent()) {
              clearVisualSmsEnrollment();
              toast.success(t('security.mfa.smsRemovedToast'));
            }
            if (result === 'backup') {
              return;
            }
            onCreatedRef.current();
          } catch {
            setPhase('failed');
          }
        },
        () => {
          inFlightRef.current = false;
        },
      );
    })();
  };

  if (phase === 'failed') {
    return (
      <MfaInvalidCodeDialog
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.PASSKEY_INVALID}
        description={t('security.mfa.passkeyFailedDescription')}
        isMobile={isMobile}
        onClose={onCancel}
        primaryAction={{
          dataTestId: MFA.PASSKEY_TRY_AGAIN,
          label: t('security.mfa.totpTryAgain'),
          onClick: () => setPhase('intro'),
        }}
        secondaryAction={{ dataTestId: MFA.CANCEL, label: t('security.mfa.cancel'), onClick: onCancel }}
        title={t('security.mfa.passkeyFailedTitle')}
      />
    );
  }

  const waiting = (
    <SDSFlexbox
      alignItems="center"
      className="MfaPasskeyWaiting"
      dataTestId={MFA.PASSKEY_WAITING}
      fullWidth
      gap="button-regular"
      justifyContent="center"
      wrap="wrap"
    >
      <SDSInternalSpinner />
      <SDSText color="inactive" text={t('security.mfa.passkeyWaiting')} type="body-medium" />
    </SDSFlexbox>
  );

  const isCreating = phase === 'creating';

  if (isMobile) {
    return (
      <MfaSettingsPage
        actions={
          isCreating ? undefined : (
            <>
              <SDSButton
                dataTestId={MFA.TOTP_BACK}
                mode="outline"
                onClick={onBackToMethods}
                text={t('security.mfa.totpBack')}
              />
              <SDSButton
                dataTestId={MFA.PASSKEY_CREATE}
                mode="primary"
                onClick={createPasskey}
                text={t('security.mfa.passkeyCreate')}
              />
            </>
          )
        }
        ariaLabel={t('security.mfa.passkeySetupTitle')}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.PASSKEY_SETUP}
        onBack={onCancel}
      >
        <SDSFlexbox alignItems="center" direction="column" fullWidth gap="text-medium">
          <SDSText text={t('security.mfa.passkeySetupTitle')} type="h4" />
          <MfaHero plain setup src={passkeySetupIntro} />
        </SDSFlexbox>
        {isCreating ? waiting : <SDSText text={t('security.mfa.passkeySetupLead')} type="body-medium" />}
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      className="MfaTotpDialog MfaPasskeySetupDialog"
      dataTestId={MFA.PASSKEY_SETUP}
      isOpen
      onClose={onCancel}
      primaryAction={
        isCreating
          ? undefined
          : {
              dataTestId: MFA.PASSKEY_CREATE,
              label: t('security.mfa.passkeyCreate'),
              onClick: createPasskey,
            }
      }
      secondaryAction={
        isCreating
          ? undefined
          : {
              dataTestId: MFA.TOTP_BACK,
              label: t('security.mfa.totpBack'),
              onClick: onBackToMethods,
            }
      }
      showCloseButton={false}
      size="small"
    >
      <SDSFlexbox direction="column" fullWidth gap="text-x-large">
        <MfaHero setup src={passkeySetupIntro} />
        {isCreating ? (
          waiting
        ) : (
          <>
            <SDSText text={t('security.mfa.passkeySetupTitle')} type="h4" />
            <SDSText color="text-secondary" text={t('security.mfa.passkeySetupLead')} type="body-extra-large" />
          </>
        )}
      </SDSFlexbox>
    </SDSDialog>
  );
}
